import * as StringUtils from '../../utils/string-utils';
import { escapeHTML } from '../../utils/string-utils';
import appAlertTemplate from './app-alert.tpl.html';
import './app-alert.less';
import {
  RETRY_ALWAYS_STATUS_CODES,
  RETRY_ON_GET_STATUS_CODES,
} from '../../interceptors/auto-retry-interceptor/auto-retry-interceptor';
import {
  TEXT_QUESTION,
  WEIGHTED_CHOICE_QUESTION,
} from '../../models/quiz-question';
import { appConfig } from '../../constants/app-config';

const modelErrorTypeMappings = {
  [WEIGHTED_CHOICE_QUESTION]: 'Multiple Choice Questions',
  [TEXT_QUESTION]: 'Text Questions',
};

const errorMessages = {
  serverUnavailable:
    'Unable to save your changes, the server is temporarily unavailable. Please try again.',
  unknownErrorTeamMember:
    'An error has occurred and our team has been alerted. Please try again soon. If the issue persists, please reach out to us via in-app messaging or team@qualified.io.',
  unknownErrorCandidate:
    'An error has occurred and our team has been alerted. Please try again soon. If the issue persists, please reach out to the team that invited you.',
};

const serverAccessErrors = [].concat(
  RETRY_ON_GET_STATUS_CODES,
  RETRY_ALWAYS_STATUS_CODES,
);

export const appAlertComponentModule = angular
  .module('Q.components.app-alert', [])

  .directive('appAlert', function (appAlert) {
    return {
      restrict: 'AE',
      scope: {},
      template: appAlertTemplate,
      link(scope) {
        scope.appAlert = appAlert;
        scope.onLinkClick = () => {
          if (appAlert.linkAction) {
            appAlert.linkAction();
          }
          appAlert.clear();
        };
      },
    };
  })

  .factory(
    'appAlert',
    function ($timeout, $q, $log, $sce, $compile, $rootScope, $document) {
      let lastCloser;
      let appAlertEl;

      const alert = {
        active: false,
        style: null,
        message: null,
        timer: null,
        clear,
        show(msg, type) {
          if (!angular.isFunction(alert[type])) {
            type = 'info';
          }
          alert[type](msg);
        },
      };

      ['success', 'info', 'warning', 'danger'].forEach((s) => {
        alert[s] = _show.bind(alert, s);
      });
      alert.error = alert.danger;

      /**
       * Returns an error message based on some unknown error object
       * @param {*} resp - Error object
       * @param {Object} options - Additional Options
       * @param {string} options.prefix - Prefix for returned message
       * @returns {{message: string, status: string|boolean}} Message and the error status code (or false if none)
       */
      alert.modelErrorMessage = function (resp, options = {}) {
        const errors = [];

        let data = resp;
        if (data && data.data) {
          // so we can pass responses directly to this method
          data = data.data;
        }

        function pushError(field, msg) {
          field = StringUtils.fieldToLabel(field);
          let type = '';
          if (data && modelErrorTypeMappings[data.type]) {
            type = modelErrorTypeMappings[data.type] + ': ';
          }
          errors.push(`${type}${field} ${msg}`);
        }

        let message = false;
        let status = false;

        if (!data) {
          message = errorMessages.serverUnavailable;
        } else if (
          angular.isString(data) &&
          !/<html>/.test(data) &&
          (resp.status === undefined || resp.status < 500)
        ) {
          // don't show raw 5xx errors in the alert
          message = data;
        } else if (data.reason === 'ValidationErrors') {
          // each field is returned with its own array of error messages
          angular.forEach(data.errors, (msgs, field) => {
            if (angular.isObject(msgs)) {
              angular.forEach(msgs, (msg) => {
                // embedded documents can also have their errors included, so we need to
                // go another level deep in this case
                if (angular.isObject(msg)) {
                  angular.forEach(msg, (msg, field) => {
                    pushError(field, msg);
                  });
                } else {
                  pushError(field, msg);
                }
              });
            } else {
              pushError(field, msgs);
            }
          });

          message = errors.join(', ');
        } else if (serverAccessErrors.includes(resp.status)) {
          message = errorMessages.serverUnavailable;
          status = resp.status;
        } else {
          if (resp instanceof Error) {
            // in case something else is triggering the error
            $log.error(resp);
            message = resp.toString();
            if (resp.cause) {
              message += ` (${resp.cause})`;
            }
          } else if (data.reason) {
            message = data.reason;
          } else {
            if (resp.statusText) {
              message = resp.statusText;
            } else if (appConfig.candidateApp) {
              message = errorMessages.unknownErrorCandidate;
            } else {
              message = errorMessages.unknownErrorTeamMember;
            }
            status = resp.status;
          }
        }
        if (options.prefix) {
          message = `${options.prefix} ${message}`;
        }
        return { message, status };
      };

      /**
       * Handles rejected promises automatically, or can be used independently
       * @param {*} resp - Error object
       * @param {Object} options - Additional Options
       * @param {string} options.prefix - Prefix for returned message
       * @returns {PromiseRejectedResult} Rejected promise with original response
       */
      alert.modelError = function (resp, options = {}) {
        if (resp && !resp.didAlert) {
          let { message, status } = alert.modelErrorMessage(resp, options);
          if (status) {
            message = $sce.trustAsHtml(
              `${escapeHTML(message)} <span class="extra-detail">(${status})</span>`,
            );
          }
          alert.error(message, options);
        }

        return $q.reject(resp);
      };

      //
      /**
       * Enables providing options to the modelError function
       * @param {Object} options - Additional Options
       * @param {string} options.prefix - Prefix for returned message
       * @returns {Function} Rejected promise handler
       */
      alert.modelError.with = function (options) {
        return (resp) => alert.modelError(resp, options);
      };

      function clear() {
        alert.active = false;
        _cancelTimer();
        // delay so the fade looks OK
        $timeout(() => {
          if (!alert.active) {
            alert.message = null;
            alert.htmlMessage = null;
            alert.link = null;
            alert.linkTitle = null;
            alert.action = null;
          }
        }, 150);
      }

      function _cancelTimer() {
        if (alert.timer) $timeout.cancel(alert.timer);
        alert.timer = null;
      }

      /**
       * Shows a message in the given style
       * @param style {String} Style for the alert
       * @param msg {String} Message to show
       * @param options {Number|boolean|Object?} If a number or boolean, represents the timeout, otherwise an object with the following
       * @param options.showFor {Number|boolean?} Delay, or false to show until canceled with appAlert.clear()
       * @param options.linkTitle {String?} Title for the link
       * @param options.link {String?} uiSref-style link for the link
       * @param options.linkAction {Function} Function to call if the link is clicked
       * @returns {Promise}
       * @private
       */
      function _show(style, msg, options = {}) {
        if (!appAlertEl) {
          appAlertEl = $compile('<app-alert></app-alert>')($rootScope);
          $document.find('body').append(appAlertEl);
        }

        _cancelTimer();

        alert.style = style;
        if (angular.isString(msg)) {
          alert.message = msg;
          alert.htmlMessage = '';
        } else {
          alert.message = '';
          alert.htmlMessage = msg;
        }

        if (!angular.isObject(options)) {
          options = { showFor: options };
        }
        let {
          showFor = style === 'danger' ? 0 : 6000,
          linkTitle = null,
          link = null,
          linkAction = null,
        } = options;

        alert.linkTitle = linkTitle;
        alert.link = link;
        alert.linkAction = linkAction;
        if (!alert.active) {
          $timeout(() => (alert.active = true));
        }
        alert.closeable = showFor !== false;
        let promise;
        if (showFor > 0) {
          alert.timer = $timeout(clear, showFor);
          promise = lastCloser = alert.timer;
        } else {
          promise = lastCloser = $q.when();
        }
        promise.close = () => {
          if (lastCloser === promise) {
            alert.clear();
          }
        };
        return promise;
      }

      return alert;
    },
  );
